import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { EncryptedStore } from '../lib/store/EncryptedStore';
import { 
  AppState, 
  AuthState, 
  Transaction, 
  Account, 
  Category, 
  UUID,
  Household,
  Member,
  BudgetAllocation 
} from '../lib/types';

// Action types
type AppAction = 
  | { type: 'SET_AUTH_STATE'; payload: AuthState }
  | { type: 'SET_CURRENT_MONTH'; payload: string }
  | { type: 'SET_SELECTED_ACCOUNT'; payload: UUID | undefined }
  | { type: 'SET_SELECTED_CATEGORY'; payload: UUID | undefined }
  | { type: 'TOGGLE_HIDDEN_CATEGORIES' }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' | 'system' }
  | { type: 'LOCK_APPLICATION' }
  | { type: 'UNLOCK_APPLICATION'; payload: { household: Household; currentUser: Member } };

// Initial state
const initialState: AppState = {
  auth: {
    isUnlocked: false,
    lastActivity: Date.now()
  },
  currentMonth: new Date().toISOString().slice(0, 7), // YYYY-MM
  selectedAccount: undefined,
  selectedCategory: undefined,
  showHiddenCategories: false,
  theme: 'dark' // Default to dark mode for better UX
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_AUTH_STATE':
      return {
        ...state,
        auth: action.payload
      };
    case 'SET_CURRENT_MONTH':
      return {
        ...state,
        currentMonth: action.payload
      };
    case 'SET_SELECTED_ACCOUNT':
      return {
        ...state,
        selectedAccount: action.payload
      };
    case 'SET_SELECTED_CATEGORY':
      return {
        ...state,
        selectedCategory: action.payload
      };
    case 'TOGGLE_HIDDEN_CATEGORIES':
      return {
        ...state,
        showHiddenCategories: !state.showHiddenCategories
      };
    case 'SET_THEME':
      return {
        ...state,
        theme: action.payload
      };
    case 'LOCK_APPLICATION':
      return {
        ...state,
        auth: {
          isUnlocked: false,
          lastActivity: Date.now()
        }
      };
    case 'UNLOCK_APPLICATION':
      return {
        ...state,
        auth: {
          isUnlocked: true,
          household: action.payload.household,
          currentUser: action.payload.currentUser,
          lastActivity: Date.now()
        }
      };
    default:
      return state;
  }
}

// Context types
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  storage: EncryptedStore;
  
  // Actions
  createLedger: (passphrase: string, householdName: string, ownerName: string) => Promise<boolean>;
  unlockLedger: (passphrase: string) => Promise<boolean>;
  lockLedger: () => void;
  
  // Data operations
  addTransaction: (transaction: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => void;
  addAccount: (account: Omit<Account, 'id'>) => void;
  addCategory: (category: Omit<Category, 'id'>) => void;
  updateBudgetAllocation: (categoryId: string, month: string, allocated: number) => void;
  
  // Getters
  getAccounts: () => Account[];
  getCategories: () => Category[];
  getTransactions: () => Transaction[];
  getBudgetAllocations: () => BudgetAllocation[];
}

const AppContext = createContext<AppContextType | null>(null);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const storage = EncryptedStore.getInstance();

  // Theme handling
  useEffect(() => {
    const root = window.document.documentElement;
    
    if (state.theme === 'dark') {
      root.classList.add('dark');
    } else if (state.theme === 'light') {
      root.classList.remove('dark');
    } else {
      // System theme
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      if (mediaQuery.matches) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
  }, [state.theme]);

  // Actions
  const createLedger = async (passphrase: string, householdName: string, ownerName: string): Promise<boolean> => {
    const success = await storage.createLedger(passphrase, householdName, ownerName);
    
    if (success) {
      const household = storage.getHousehold()!;
      dispatch({
        type: 'UNLOCK_APPLICATION',
        payload: {
          household,
          currentUser: household.members[0]
        }
      });
    }

    return success;
  };

  const unlockLedger = async (passphrase: string): Promise<boolean> => {
    const success = await storage.unlockLedger(passphrase);
    
    if (success) {
      const household = storage.getHousehold()!;
      dispatch({
        type: 'UNLOCK_APPLICATION',
        payload: {
          household,
          currentUser: household.members[0]
        }
      });
    }

    return success;
  };

  const lockLedger = () => {
    storage.lock();
    dispatch({ type: 'LOCK_APPLICATION' });
  };

  // Data operations
  const addTransaction = (transaction: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => {
    if (!state.auth.currentUser) return;
    
    storage.addTransaction(transaction);
  };

  const addAccount = (account: Omit<Account, 'id'>) => {
    storage.addAccount(account);
  };

  const addCategory = (category: Omit<Category, 'id'>) => {
    storage.addCategory(category);
  };

  const updateBudgetAllocation = (categoryId: string, month: string, allocated: number) => {
    storage.updateBudgetAllocation(categoryId, month, allocated);
  };

  // Getters
  const getAccounts = (): Account[] => {
    return storage.getAccounts();
  };

  const getCategories = (): Category[] => {
    return storage.getCategories();
  };

  const getTransactions = (): Transaction[] => {
    return storage.getTransactions();
  };

  const getBudgetAllocations = (): BudgetAllocation[] => {
    return storage.getBudgetAllocations();
  };

  const contextValue: AppContextType = {
    state,
    dispatch,
    storage,
    
    // Actions
    createLedger,
    unlockLedger,
    lockLedger,
    
    // Data operations
    addTransaction,
    addAccount,
    addCategory,
    updateBudgetAllocation,
    
    // Getters
    getAccounts,
    getCategories,
    getTransactions,
    getBudgetAllocations
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

// Hook to use the context
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export { AppContext };